/**
 * WarehouseExecDashboard — Warehouse Executive (dock/dispatch operator).
 *
 * Ported from `WarehouseExecDashboard` in aura-exec.jsx.
 * GRN queue (inbound) + Dispatch queue (outbound) side-by-side + continuous
 * barcode scan strip + daily register EOD + cycle count tasks + discrepancy flag.
 *
 * UI-only. Tenant-safe.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Truck, Sparkles, Users, ScanLine, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { safeGet, formatNum, todayLabel, makeCreditBurn } from './_execShared';

// ─── Types ────────────────────────────────────────────────────────────────

interface GrnRow {
  id: string;
  number?: string;
  vendor?: { name?: string } | string;
  vendorName?: string;
  expectedAt?: string;
  expectedLabel?: string;
  itemCount?: number;
  items?: number;
  flagged?: boolean;
  flag?: boolean;
}

interface DispatchRow {
  id: string;
  number?: string;
  salesOrderNumber?: string;
  salesOrder?: { orderNumber?: string; account?: { name?: string } };
  address?: string;
  mode?: string;
  transportMode?: string;
  itemCount?: number;
  items?: number;
  status?: 'Pack' | 'Printed' | 'Loading' | string;
}

interface CycleCountTask {
  id: string;
  zone?: string;
  zoneName?: string;
  dueDate?: string;
  dueLabel?: string;
  status?: string;
}

interface DailyStats {
  grnsDone?: number;
  dispatched?: number;
  discrepancies?: number;
  unitsMoved?: number;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: bg, color, fontWeight: 500 }}>
      {children}
    </span>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function WarehouseExecDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [grns, setGrns] = useState<GrnRow[]>([]);
  const [grnsLoading, setGrnsLoading] = useState(true);
  const [grnsErr, setGrnsErr] = useState<string | null>(null);

  const [dispatches, setDispatches] = useState<DispatchRow[]>([]);
  const [dispatchesLoading, setDispatchesLoading] = useState(true);

  const [cycleCounts, setCycleCounts] = useState<CycleCountTask[]>([]);
  const [cycleLoading, setCycleLoading] = useState(true);

  const [stats, setStats] = useState<DailyStats | null>(null);

  const burn = makeCreditBurn();

  useEffect(() => {
    safeGet<{ items?: GrnRow[] } | GrnRow[]>('/warehouse/grn/today').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: GrnRow[] } | null)?.items ?? [];
      setGrns(parsed);
      setGrnsErr(r.error);
      setGrnsLoading(false);
    });

    safeGet<{ items?: DispatchRow[] } | DispatchRow[]>(
      '/warehouse/dispatch-slips/today?status=open',
    ).then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: DispatchRow[] } | null)?.items ?? [];
      setDispatches(parsed);
      setDispatchesLoading(false);
    });

    safeGet<{ items?: CycleCountTask[] } | CycleCountTask[]>(
      '/warehouse/cycle-count/assigned?userId=me',
    ).then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: CycleCountTask[] } | null)?.items ?? [];
      setCycleCounts(parsed);
      setCycleLoading(false);
    });

    safeGet<DailyStats>('/role-kpis/warehouse/daily').then((r) => setStats(r.data));
  }, []);

  const flaggedCount = grns.filter((g) => g.flagged || g.flag).length;

  const getVendor = (g: GrnRow) =>
    g.vendorName ?? (typeof g.vendor === 'string' ? g.vendor : g.vendor?.name) ?? '—';
  const getItemCount = (g: GrnRow) => g.itemCount ?? g.items ?? 0;
  const getExpected = (g: GrnRow) =>
    g.expectedLabel ??
    (g.expectedAt
      ? new Date(g.expectedAt).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—');

  const getSoLabel = (d: DispatchRow) =>
    d.salesOrderNumber ??
    d.salesOrder?.account?.name ??
    d.salesOrder?.orderNumber ??
    '—';
  const getDispatchItems = (d: DispatchRow) => d.itemCount ?? d.items ?? 0;
  const getMode = (d: DispatchRow) => d.mode ?? d.transportMode ?? '—';
  const statusColor: Record<string, string> = {
    Pack: 'var(--saffron-600)',
    Printed: 'var(--info)',
    Loading: 'var(--forest-600)',
  };

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Warehouse Executive · Dock ops
          </div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500 }}>
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {todayLabel()} · {grns.length} GRN{grns.length === 1 ? '' : 's'} ·{' '}
            {dispatches.length} dispatch{dispatches.length === 1 ? '' : 'es'}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See team view
          </button>
        )}
      </div>

      {/* Barcode scan strip */}
      <div
        className="card"
        style={{
          padding: 14,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: 'linear-gradient(135deg, var(--ivory-2), var(--ivory-0))',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ScanLine size={22} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Barcode scan · continuous mode</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Point scanner at any SKU — auto-routes to GRN or Dispatch based on workflow state
          </div>
        </div>
        {/* Apr 27 (audit P0) — wired to barcode scan management page. */}
        <button
          className="btn"
          onClick={() => navigate('/manufacturing/barcodes')}
          style={{
            padding: '10px 18px',
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            border: '1px solid var(--ink-0)',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Start scan session
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* GRN queue */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>GRN queue</h3>
            {flaggedCount > 0 && (
              <span style={{ marginLeft: 8 }}>
                <Chip color="var(--danger, #B8563B)" bg="var(--danger-bg, #FBEAE6)">
                  {flaggedCount} flagged
                </Chip>
              </span>
            )}
            <div style={{ flex: 1 }} />
            {/* Apr 27 (audit P1) — wire to inward dashboard with action=new query. */}
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/warehouse/inward?action=new')}
            >
              New GRN
            </button>
          </div>
          {grnsLoading ? (
            <Skeleton h={180} />
          ) : grnsErr ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="GRN queue unavailable" />
            </div>
          ) : grns.length === 0 ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="No GRNs expected today" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {grns.map((g) => (
                <div
                  key={g.id}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 10,
                    borderLeft: `3px solid ${
                      g.flagged || g.flag ? 'var(--danger, #B8563B)' : 'transparent'
                    }`,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 6,
                      background: 'var(--ivory-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Download size={16} color="var(--ink-2)" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {g.number ?? g.id}
                      <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}> · {getVendor(g)}</span>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        marginTop: 2,
                      }}
                    >
                      <span>{formatNum(getItemCount(g))} lines</span>
                      <span>·</span>
                      <span>Expected {getExpected(g)}</span>
                      {(g.flagged || g.flag) && (
                        <>
                          <span>·</span>
                          <span style={{ color: 'var(--danger, #B8563B)', fontWeight: 500 }}>
                            ⚠ discrepancy last GRN
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => navigate('/warehouse/inward')}
                  >
                    PO ref
                  </button>
                  {/* Apr 27 (audit P0) — wired Receive to inward dashboard. */}
                  <button
                    className="btn btn-sm"
                    onClick={() => navigate(`/warehouse/inward?grn=${encodeURIComponent(g.id)}`)}
                    style={{
                      background: 'var(--ink-0)',
                      color: 'var(--ivory-0)',
                      border: '1px solid var(--ink-0)',
                    }}
                  >
                    Receive
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dispatch queue */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>Dispatch queue</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{dispatches.length} pending</Chip>
            </span>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ai btn-sm"
              onClick={() => burn(6, 'print_all_slips')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Sparkles size={11} /> Print all · 6 cr
            </button>
          </div>
          {dispatchesLoading ? (
            <Skeleton h={180} />
          ) : dispatches.length === 0 ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="No dispatches pending today" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dispatches.map((d) => (
                <div
                  key={d.id}
                  className="card"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10 }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 6,
                      background: 'var(--ivory-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Truck size={16} color="var(--ink-2)" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {d.number ?? d.id}
                      <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
                        {' '}
                        · {getSoLabel(d)}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        marginTop: 2,
                      }}
                    >
                      <span>{getDispatchItems(d)} items</span>
                      {d.address && (
                        <>
                          <span>·</span>
                          <span>{d.address}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{getMode(d)}</span>
                    </div>
                    {d.status && (
                      <div style={{ marginTop: 4 }}>
                        <Chip
                          color={statusColor[d.status] ?? 'var(--ink-3)'}
                          bg={`${statusColor[d.status] ?? 'var(--ivory-2)'}26`}
                        >
                          ● {d.status}
                        </Chip>
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => navigate('/warehouse/barcode-print')}
                  >
                    Print
                  </button>
                  {/* Apr 27 (audit P0) — wired Pack to manufacturing dispatch. */}
                  <button
                    className="btn btn-sm"
                    onClick={() => navigate(`/manufacturing/dispatch?ref=${encodeURIComponent(d.id)}`)}
                    style={{
                      background: 'var(--ink-0)',
                      color: 'var(--ivory-0)',
                      border: '1px solid var(--ink-0)',
                    }}
                  >
                    Pack
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom: EOD register + cycle count */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 320px',
          gap: 14,
          marginTop: 14,
        }}
      >
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 500 }}>
                Daily register · auto-closes at 17:00
              </h3>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                Receiving + dispatch activity for signature + upload
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/warehouse/dispatch-register')}
            >
              Preview
            </button>
            {/* Apr 27 (audit P0) — wired Close now to dispatch register. */}
            <button
              className="btn btn-sm"
              onClick={() => navigate('/warehouse/dispatch-register?action=close')}
              style={{
                marginLeft: 6,
                background: 'var(--ink-0)',
                color: 'var(--ivory-0)',
                border: '1px solid var(--ink-0)',
              }}
            >
              Close now
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
              fontSize: 12,
            }}
          >
            <div>
              <div className="num-display" style={{ fontSize: 20 }}>
                {formatNum(stats?.grnsDone ?? 0)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>GRNs done</div>
            </div>
            <div>
              <div className="num-display" style={{ fontSize: 20, color: 'var(--forest-600)' }}>
                {formatNum(stats?.dispatched ?? 0)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Dispatched</div>
            </div>
            <div>
              <div
                className="num-display"
                style={{
                  fontSize: 20,
                  color: (stats?.discrepancies ?? 0) > 0 ? 'var(--danger, #B8563B)' : undefined,
                }}
              >
                {formatNum(stats?.discrepancies ?? 0)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Discrepancies</div>
            </div>
            <div>
              <div className="num-display" style={{ fontSize: 20 }}>
                {formatNum(stats?.unitsMoved ?? 0)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Units moved</div>
            </div>
          </div>
          <button
            className="btn btn-outline btn-sm"
            style={{
              marginTop: 14,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--danger, #B8563B)',
              borderColor: '#EFCFCA',
            }}
          >
            <AlertTriangle size={12} /> Flag discrepancy
          </button>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div className="kpi-label" style={{ marginBottom: 10 }}>
            Cycle count tasks
          </div>
          {cycleLoading ? (
            <Skeleton h={90} />
          ) : cycleCounts.length === 0 ? (
            <NoData label="No cycle count tasks assigned" />
          ) : (
            cycleCounts.map((t, i) => (
              <div
                key={t.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: i < cycleCounts.length - 1 ? '1px solid var(--line)' : 'none',
                }}
              >
                <input type="checkbox" style={{ accentColor: 'var(--ink-0)' }} />
                <div style={{ flex: 1, fontSize: 12 }}>
                  {t.zoneName ?? t.zone ?? '—'}
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                    {t.dueLabel ?? (t.dueDate ? new Date(t.dueDate).toLocaleDateString('en-IN') : '—')}
                  </div>
                </div>
                {/* Apr 27 (audit P1) — wired to stock audit page with task ID query. */}
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => navigate(`/warehouse/stock-audit?task=${encodeURIComponent(t.id)}`)}
                >
                  Count
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default WarehouseExecDashboard;
